import React, { useState } from "react";
import { Link } from "react-router-dom";
import ListingThumbnail from "./ListingThumbnail";
import ReportButton from "./ReportButton";
import ReviewStars from "./ReviewStars";
import DomainStatusBadge from "./DomainStatusBadge";
import { CATEGORY_LABEL, realTagline, compactCount, displayHost } from "../../../lib/marketplace";

const CATEGORY_ACCENT = {
  ai: "text-purple-200 bg-purple-500/15",
  saas: "text-sky-200 bg-sky-500/15",
  "developer-tool": "text-lime-200 bg-lime-500/15",
  productivity: "text-amber-200 bg-amber-500/15",
  ecommerce: "text-pink-200 bg-pink-500/15",
  mobile: "text-cyan-200 bg-cyan-500/15",
  other: "text-silver-medium bg-white/8",
};

/**
 * A marketplace capsule, built on Steam's store-card anatomy: a wide 16:9 capsule that
 * carries the art, the title sitting ON the art rather than competing with it below,
 * and a dense metadata strip underneath. Sharp-edged throughout — the corner radius is
 * what made the previous version read as a settings panel rather than a storefront.
 *
 * Hover is a single state lifted to this component so the thumbnail's frame-cycling,
 * the border glow and the action strip all animate together as one object.
 *
 * Nested anchors are invalid HTML and Safari drops the inner one, so this is a <div>
 * containing sibling links rather than a link wrapping links.
 */
export default function AppCard({ listing }) {
  const [hovered, setHovered] = useState(false);

  const accent = CATEGORY_ACCENT[listing.category] || CATEGORY_ACCENT.other;
  const tagline = realTagline(listing);
  const host = displayHost(listing.appUrl);
  const isAndroid = listing.platform === "android" || listing.platform === "cross-platform";
  const tags = (listing.tags || []).slice(0, 3);

  return (
    <div
      onMouseEnter={() => setHovered(true)}
      onMouseLeave={() => setHovered(false)}
      onFocus={() => setHovered(true)}
      onBlur={() => setHovered(false)}
      className={`group relative flex flex-col bg-[#0e0e12] ring-1 transition-all duration-200 ${
        hovered
          ? "-translate-y-1 shadow-[0_16px_40px_-16px_rgba(163,230,53,0.35)] ring-lime-400/50"
          : "ring-white/10"
      }`}
    >
      <div className="relative">
        <Link
          to={`/limelite/marketplace/${listing.slug}`}
          className="block focus:outline-none focus-visible:ring-2 focus-visible:ring-lime-400"
          aria-label={`${listing.name} — view details`}
        >
          <ListingThumbnail listing={listing} hovered={hovered} />
        </Link>

        {listing.featured && (
          <span className="pointer-events-none absolute left-0 top-0 bg-amber-400 px-2 py-1 text-[10px] font-bold uppercase tracking-widest text-black">
            Featured
          </span>
        )}

        {/* Report sits on the art, revealed on hover, so the resting card stays clean. */}
        <div
          className={`absolute right-1.5 top-1.5 transition-opacity duration-200 ${
            hovered ? "opacity-100" : "opacity-0"
          }`}
        >
          <ReportButton slug={listing.slug} name={listing.name} compact />
        </div>

        <div className="pointer-events-none absolute inset-x-0 bottom-0 px-3 pb-2.5">
          <h3 className="truncate text-[15px] font-bold leading-tight text-white drop-shadow-[0_2px_6px_rgba(0,0,0,0.95)]">
            {listing.name}
          </h3>
          {host && (
            <p className="truncate font-mono text-[10px] text-white/45">{host}</p>
          )}
        </div>
      </div>

      {/* Body is deliberately compact. With placeholder taglines suppressed most cards
          have no prose at all, and the first live version left a band of dead space
          where it would have been. Chips carry the metadata; the action strip is
          pinned directly under them rather than pushed to the bottom of a tall box. */}
      <div className="flex flex-1 flex-col gap-2 px-3 pb-3 pt-2.5">
        {tagline && (
          <p className="line-clamp-2 text-xs leading-relaxed text-silver-dark">{tagline}</p>
        )}

        <div className="flex flex-wrap items-center gap-1">
          <span className={`px-1.5 py-0.5 text-[10px] font-medium ${accent}`}>
            {CATEGORY_LABEL[listing.category] || listing.category}
          </span>
          {listing.platform && listing.platform !== "web" && (
            <span className="bg-white/8 px-1.5 py-0.5 text-[10px] capitalize text-silver-medium">
              {listing.platform}
            </span>
          )}
          {tags.map((t) => (
            <span key={t} className="bg-white/5 px-1.5 py-0.5 text-[10px] text-silver-dark">
              {t}
            </span>
          ))}
          {listing.viewCount > 0 && (
            <span className="ml-auto text-[10px] tabular-nums text-silver-dark/60">
              {compactCount(listing.viewCount)} views
            </span>
          )}
          {listing.ratingCount > 0 && <ReviewStars average={listing.ratingAverage} count={listing.ratingCount} compact />}
          <DomainStatusBadge listing={listing} compact />
        </div>

        <div className="mt-auto flex items-stretch gap-1.5 pt-0.5">
          {listing.appUrl && (
            <a
              href={listing.appUrl}
              target="_blank"
              rel="noopener noreferrer"
              // Quiet at rest, lime on hover. Two failed attempts got us here: bg-white/8
              // was invisible against #0e0e12 (read as disabled), and a permanently solid
              // lime button turned a 14-card grid into 14 shouting green bars competing
              // with each other. Steam capsules are art + title + tags; the call to
              // action belongs to the card you are actually pointing at.
              className={`flex-1 py-2 text-center text-xs font-semibold transition-colors ${
                hovered
                  ? "bg-lime-400 text-black"
                  : "text-silver-light ring-1 ring-inset ring-white/15"
              }`}
            >
              Open
            </a>
          )}
          {isAndroid && listing.apkUrl && (
            <a
              href={listing.apkUrl}
              className="px-3 py-2 text-center text-xs font-medium text-lime-300 ring-1 ring-lime-500/40 transition-colors hover:bg-lime-500/15"
            >
              APK
            </a>
          )}
          <Link
            to={`/limelite/marketplace/${listing.slug}`}
            className="px-3 py-2 text-xs font-medium text-silver-medium ring-1 ring-white/12 transition-colors hover:text-silver-light hover:ring-white/30"
          >
            Details
          </Link>
        </div>
      </div>
    </div>
  );
}
